import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { showToast } from '../../utils/errorHandler';

interface SearchHeaderProps {
  onSearch: (query: string) => void;
  isLoading: boolean;
  trendsSlot?: React.ReactNode;
}

// Shuffle array helper (Fisher-Yates)
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const exampleColor = "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200 hover:text-slate-700";

/**
 * Strip a leading emoji cluster from i18n example strings (search uses clean
 * text). Handles plain pictographs, flags (regional indicators), and ZWJ
 * sequences like 🧑‍🏫 so no broken fragment leaks into the query.
 */
const stripLeadingEmoji = (text: string): string =>
  text
    .replace(/^(?:[\p{Extended_Pictographic}\p{Regional_Indicator}]|[\u200d\uFE0F\u{1F3FB}-\u{1F3FF}\s])+/u, '')
    .trim();

const SearchHeader: React.FC<SearchHeaderProps> = ({ onSearch, isLoading, trendsSlot }) => {
  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedQuery = query.trim();
    
    if (!trimmedQuery) return;
    
    // Validate length
    if (trimmedQuery.length > 500) {
      showToast(t('errors.inputTooLong', { max: 500 }), 'error');
      return;
    }
    
    onSearch(trimmedQuery);
  };

  // Pool of all examples - randomly select 5
  // Note: some numbers are skipped (72, 73, 82 were removed)
  const getRandomExamples = () => {
    const skipKeys = [77, 80, 82, 88]; // Removed prompts
    const allKeys = Array.from({ length: 120 }, (_, i) => i + 1)
      .filter(n => !skipKeys.includes(n))
      .map(n => `example${n}`);
    return shuffleArray(allKeys).slice(0, 5);
  };
  
  const [selectedKeys, setSelectedKeys] = useState(getRandomExamples);
  
  const refreshExamples = () => {
    setSelectedKeys(getRandomExamples());
  };
  
  // Build example queries from selected keys. `label` keeps the emoji for a
  // friendlier chip; `query` is the clean text we actually search with.
  const exampleQueries = selectedKeys.map((key) => {
    const raw = t(`explore.searchHeader.${key}`);
    return {
      label: raw,
      query: stripLeadingEmoji(raw),
      color: exampleColor,
    };
  });

  const handleExampleClick = (example: { label: string; query: string; color: string }) => {
    setQuery(example.query);
    onSearch(example.query);
  };

  return (
    <motion.div 
      className="relative z-[100] bg-white/60 backdrop-blur-sm border-b border-slate-200 px-4 sm:px-6 py-6 sm:py-8"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Trends Chip */}
      {trendsSlot && (
        <div className="mb-4 w-full max-w-6xl mx-auto">
          {trendsSlot}
        </div>
      )}
      
      <div className="max-w-4xl mx-auto">
        
        {/* Title */}
        <motion.h1 
          className="text-xl sm:text-2xl lg:text-[1.75rem] font-bold text-slate-800 text-center mb-4 sm:mb-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {t('explore.searchHeader.title')} {t('explore.searchHeader.titleHighlight')} {t('explore.searchHeader.titleSuffix')}
        </motion.h1>
        
        {/* Search Form */}
        <motion.form 
          onSubmit={handleSubmit}
          className="relative max-w-3xl mx-auto mb-4"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <div className="relative min-h-[36px] sm:min-h-11 flex items-start rounded-xl sm:rounded-2xl
                          border-2 border-slate-300
                          hover:shadow-xl hover:border-slate-400
                          focus-within:shadow-2xl focus-within:border-slate-500
                          transition-all duration-200">
          <textarea
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
              // Shift+Enter = new line (default behavior)
            }}
            placeholder={t('explore.searchHeader.placeholder')}
            maxLength={500}
            rows={1}
            className="w-full min-h-[36px] sm:min-h-11 bg-white
                     rounded-xl sm:rounded-2xl px-3 sm:px-5 pt-[10px] pb-[4px] sm:py-2.5 pr-16 sm:pr-16 text-xs sm:text-base placeholder-slate-400 text-slate-900
                     focus:outline-none resize-none overflow-hidden
                     transition-all duration-200"
            style={{ 
              height: 'auto',
              maxHeight: '120px'
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = target.scrollHeight + 'px';
            }}
          />
            
            {/* Clear Button */}
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  if (inputRef.current) {
                    inputRef.current.style.height = 'auto';
                    inputRef.current.focus();
                  }
                }}
                className="absolute right-[46px] sm:right-[54px] top-1/2 -translate-y-1/2 p-1 text-slate-300 hover:text-slate-500 transition-colors"
              >
                <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            )}

            {/* Search Button */}
            <motion.button
              type="submit"
              disabled={isLoading || !query.trim()}
              className={`absolute right-[3px] top-[3px] h-[30px] sm:h-[38px]
                       rounded-lg sm:rounded-xl px-3 sm:px-3.5 font-medium flex items-center justify-center text-white
                       transition-all duration-100 hover:scale-105
                       ${isLoading || !query.trim()
                         ? 'bg-slate-300 cursor-not-allowed'
                         : 'bg-slate-400 hover:bg-slate-500 shadow-md hover:shadow-lg'
                       }`}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.15 }}
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
              ) : (
                <Search className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </motion.button>
          </div>
        </motion.form>
        
        {/* Example Queries */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <p className="text-slate-500 text-xs sm:text-sm">{t('explore.searchHeader.tryExamples')}</p>
            <button
              onClick={refreshExamples}
              className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all duration-200"
              title="New examples"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {exampleQueries.map((example, index) => (
              <motion.button
                key={example.label}
                onClick={() => handleExampleClick(example)}
                className={`${example.color} 
                         px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-[13px] font-medium border
                         transition-all duration-100 hover:shadow-sm`}
                disabled={isLoading}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  delay: 0.6 + (index * 0.1), 
                  duration: 0.3,
                  ease: "backOut"
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {example.label}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SearchHeader;
